<template><div><code-well-header>
  <div class="d-d-grid d-g16 d-g-cols2 d-w100p">
    <dt-input label="Label" placeholder="Placeholder" />
    <dt-input label="Label" type="textarea" placeholder="Placeholder" />
  </div>
</code-well-header>
<!-- <component-combinator component-name="DtInput" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">This component combines both the <code v-pre>input</code> and <code v-pre>textarea</code> elements as options within a single component. Its default presentation includes a paired text <code v-pre>label</code>.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">If you can’t reasonably predict a user’s answer to a prompt and there might be wide variability in users’ answers.</li>
<li class="d-docsite--list-element">When using another type of input will make answering more difficult. For example, birthdays and other known dates are easier to type in than they are to select from a calendar picker.</li>
<li class="d-docsite--list-element">When users want to be able to paste in a response.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">When users are choosing from a specific set of options. Consider <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select</RouteLink>, <RouteLink to="/components/radio.html" class="d-docsite--link d-link">Radio</RouteLink>, or <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink>.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The length of the text input provides a hint to users as to how much text to enter.</li>
<li class="d-docsite--list-element">Only show error validation messages or stylings after a user has interacted with a particular field.</li>
<li class="d-docsite--list-element">Do not use placeholder text (i.e. <code v-pre>placeholder</code> attribute) in place of an accessible <code v-pre>label</code>.</li>
<li class="d-docsite--list-element">Consider the type of content a user may enter to aid mobile device entry; mobile devices typically surface a keyboard UI attuned to the type. For example, type=&quot;tel&quot; will surface a <a href="http://html5doctor.com/html5-forms-input-types/#input-tel" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">phone keyboard</a>.</li>
</ul>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We offer different sizes for instances in which the interface requires a smaller or larger input. In general, though, use the base (medium) size input as much as possible, especially in forms.</p>
<code-well-header>
  <div class="d-d-grid d-g16 d-g-cols2 d-w100p">
    <dt-input label="Extra Small" type="text" placeholder="Placeholder" size="xs" />
    <dt-input label="Extra Small" type="textarea" placeholder="Placeholder" size="xs" />
    <dt-input label="Small" type="text" placeholder="Placeholder" size="sm" />
    <dt-input label="Small" type="textarea" placeholder="Placeholder" size="sm" />
    <dt-input label="Medium" type="text" placeholder="Placeholder" size="md" />
    <dt-input label="Medium" type="textarea" placeholder="Placeholder" size="md" />
    <dt-input label="Large" type="text" placeholder="Placeholder" size="lg" />
    <dt-input label="Large" type="textarea" placeholder="Placeholder" size="lg" />
    <dt-input label="Extra large" type="text" placeholder="Placeholder" size="xl" />
    <dt-input label="Extra large" type="textarea" placeholder="Placeholder" size="xl" />
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label>
    <div class="d-label d-label--xs">Extra small</div>
    <div class="d-input__wrapper">
      <input type="text" class="d-input d-input--xs" placeholder="Placeholder">
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--sm">Small</div>
    <div class="d-input__wrapper">
      <input type="text" class="d-input d-input--sm" placeholder="Placeholder">
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--md">Medium</div>
    <div class="d-input__wrapper">
      <input type="text" class="d-input" placeholder="Placeholder">
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--lg">Large</div>
    <div class="d-input__wrapper">
      <input type="text" class="d-input d-input--lg" placeholder="Placeholder">
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--xl">Extra large</div>
    <div class="d-input__wrapper">
      <input type="text" class="d-input d-input--xl" placeholder="Placeholder">
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--xs">Extra small</div>
    <div class="d-input__wrapper">
      <textarea class="d-textarea d-textarea--xs" placeholder="Placeholder" />
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--sm">Small</div>
    <div class="d-input__wrapper">
      <textarea class="d-textarea d-textarea--sm" placeholder="Placeholder" />
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--md">Medium</div>
    <div class="d-input__wrapper">
      <textarea class="d-textarea" placeholder="Placeholder" />
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--lg">Large</div>
    <div class="d-input__wrapper">
      <textarea class="d-textarea d-textarea--lg" placeholder="Placeholder" />
    </div>
  </label>
</div>
<div>
  <label>
    <div class="d-label d-label--xl">Extra large</div>
    <div class="d-input__wrapper">
      <textarea class="d-textarea d-textarea--xl" placeholder="Placeholder" />
    </div>
  </label>
</div>
'
vueCode='
<dt-input label="Extra Small" type="text" placeholder="Placeholder" size="xs" />
<dt-input label="Extra Small" type="textarea" placeholder="Placeholder" size="xs" />
<dt-input label="Small" type="text" placeholder="Placeholder" size="sm" />
<dt-input label="Small" type="textarea" placeholder="Placeholder" size="sm" />
<dt-input label="Medium" type="text" placeholder="Placeholder" size="md" />
<dt-input label="Medium" type="textarea" placeholder="Placeholder" size="md" />
<dt-input label="Large" type="text" placeholder="Placeholder" size="lg" />
<dt-input label="Large" type="textarea" placeholder="Placeholder" size="lg" />
<dt-input label="Extra large" type="text" placeholder="Placeholder" size="xl" />
<dt-input label="Extra large" type="textarea" placeholder="Placeholder" size="xl" />
'
showHtmlWarning />
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">An input is normally paired with a label, but there are times when it can be used without a label.  Placeholder text should primarily be used as a content prompt and only provided when needed.</p>
<code-well-header>
  <div class="d-stack16 d-w100p">
    <dt-input label="Label" placeholder="Placeholder" />
    <dt-input label="Label" value="Value" />
    <dt-input label="Label" placeholder="Placeholder" disabled />
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label class="d-label" for="Dialtone--InputExample1a">...</label>
  <input class="d-input" id="Dialtone--InputExample1a" type="text" placeholder="..." />
</div>
<div>
  <label class="d-label" for="Dialtone--InputExample1c">Label</label>
  <input class="d-input" id="Dialtone--InputExample1c" type="text" value="Value" />
</div>
<div>
  <label class="d-label" for="Dialtone--InputExample1b">...</label>
  <input class="d-input" id="Dialtone--InputExample1b" type="text" placeholder="..." disabled />
</div>
'
vueCode='
<dt-input label="Label" placeholder="Placeholder" />
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-input label="Label" model-value="Value" />
<dt-input label="Label" placeholder="Placeholder" disabled />
'
showHtmlWarning />
<code-well-header>
  <div class="d-stack16 d-w100p">
    <dt-input label="Label" placeholder="Placeholder" type="textarea" />
    <dt-input label="Label" type="textarea" value="Value" />
    <dt-input label="Label" placeholder="Placeholder" type="textarea" disabled />
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label class="d-label" for="Dialtone--TextareaExample1a">...</label>
  <textarea class="d-textarea" id="Dialtone--TextareaExample1a" type="text" placeholder="..."></textarea>
</div>
<div>
  <label class="d-label" for="Dialtone--TextareaExample1c">Label</label>
  <textarea class="d-textarea" id="Dialtone--TextareaExample1c" type="text">Value</textarea>
</div>
<div>
  <label class="d-label" for="Dialtone--TextareaExample1b">...</label>
  <textarea class="d-textarea" id="Dialtone--TextareaExample1b" type="text" placeholder="..." disabled></textarea>
</div>
'
vueCode='
<dt-input label="Label" placeholder="Placeholder" type="textarea" />
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-input label="Label" type="textarea" model-value="Value" />
<dt-input label="Label" placeholder="Placeholder" type="textarea" disabled />
'
showHtmlWarning />
<h3 id="with-description-text" tabindex="-1" class="d-docsite--header-3">With Description Text <a class="header-anchor d-link d-docsite--link d-link" href="#with-description-text" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w100p">
    <dt-input label="Label" description="Helpful description text" placeholder="Placeholder"/>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<label class="d-label" for="Dialtone--InputExample2">...</label>
<span class="d-description">...</span>
<input class="d-input" id="Dialtone--InputExample2" type="text" placeholder="..." />
'
vueCode='
<dt-input label="Label" description="Helpful description text" placeholder="Placeholder"/>
'
showHtmlWarning />
<code-well-header>
  <div class="d-w100p">
    <dt-input label="Label" description="Helpful description text" type="textarea" placeholder="Placeholder"/>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<label class="d-label" for="Dialtone--TextareaExample">...</label>
<span class="d-description">...</span>
<textarea class="d-textarea" id="Dialtone--TextareaExample" type="text" placeholder="..."></textarea>
'
vueCode='
<dt-input label="Label" description="Helpful description text" type="textarea" placeholder="Placeholder"/>
'
showHtmlWarning />
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provides feedback to the user based on their interaction, or lack thereof, with an input. When a DtInput has an error message, it displays a red border to indicate the invalid state. The red border and error message appear when you pass a message with <code v-pre>type: &quot;error&quot;</code> to the <code v-pre>messages</code> prop. Success states show a green border, and warnings show a yellow border.</p>
<code-well-header>
  <div class="d-stack16 d-w100p">
    <dt-input label="Label" type="email" value="Value" :messages="[messages.error]"/>
    <dt-input label="Label" type="email" value="Value" :messages="[messages.success]"/>
    <dt-input label="Label" type="email" value="Value" :messages="[messages.warning]"/>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label class="d-label" for="Dialtone--InputExample3">...</label>
  <input class="d-input d-input--error" id="Dialtone--InputExample3" type="email" placeholder="..." value="..." />
  <span class="d-validation-message d-validation-message--error">...</span>
</div>
<div>
  <label class="d-label" for="Dialtone--InputExample4">...</label>
  <input class="d-input d-input--success" id="Dialtone--InputExample4" type="email" placeholder="..." value="..." />
  <span class="d-validation-message d-validation-message--success">...</span>
</div>
<div>
  <label class="d-label" for="Dialtone--InputExample5">...</label>
  <input class="d-input d-input--warning" id="Dialtone--InputExample5" type="email" placeholder="..." value="..." />
  <span class="d-validation-message d-validation-message--warning">...</span>
</div>
'
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-input label="Label" type="email" model-value="Value" :messages="[messages.error]"/>
<dt-input label="Label" type="email" model-value="Value" :messages="[messages.success]"/>
<dt-input label="Label" type="email" model-value="Value" :messages="[messages.warning]"/>
'
showHtmlWarning />
<code-well-header>
  <div class="d-stack16 d-w100p">
    <dt-input label="Label" type="textarea" value="Value" :messages="[messages.error]"/>
    <dt-input label="Label" type="textarea" value="Value" :messages="[messages.success]"/>
    <dt-input label="Label" type="textarea" value="Value" :messages="[messages.warning]"/>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label class="d-label" for="Dialtone--TextareaExample3">...</label>
  <textarea class="d-textarea d-textarea--error" id="Dialtone--TextareaExample3" type="email" placeholder="..." value="..."></textarea>
  <span class="d-validation-message d-validation-message--error">...</span>
</div>
<div>
  <label class="d-label" for="Dialtone--TextareaExample4">...</label>
  <textarea class="d-textarea d-textarea--success" id="Dialtone--TextareaExample4" type="email" placeholder="..." value="..."></textarea>
  <span class="d-validation-message d-validation-message--success">...</span>
</div>
<div>
  <label class="d-label" for="Dialtone--TextareaExample5">...</label>
  <textarea class="d-textarea d-textarea--warning" id="Dialtone--TextareaExample5" type="email" placeholder="..." value="..."></textarea>
  <span class="d-validation-message d-validation-message--warning">...</span>
</div>
'
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-input label="Label" type="textarea" model-value="Value" :messages="[messages.error]"/>
<dt-input label="Label" type="textarea" model-value="Value" :messages="[messages.success]"/>
<dt-input label="Label" type="textarea" model-value="Value" :messages="[messages.warning]"/>
'
showHtmlWarning />
<h3 id="with-multiple-validation-messages" tabindex="-1" class="d-docsite--header-3">With Multiple Validation Messages <a class="header-anchor d-link d-docsite--link d-link" href="#with-multiple-validation-messages" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w100p">
    <dt-input ref="multipleMessages" label="Label" type="email" value="Value" :messages="multipleMessages" />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.multipleMessages'
vueCode='
<!-- IMPORTANT NOTE: Change model-value to just value in Vue 2 -->
<dt-input label="Label" type="email" model-value="Value" :messages="multipleMessages" />
'
/>
<h3 id="with-maximum-length-validation" tabindex="-1" class="d-docsite--header-3">With Maximum Length Validation <a class="header-anchor d-link d-docsite--link d-link" href="#with-maximum-length-validation" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Adds validation for the input length. Make sure to provide the following props:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>currentLength</code>: the current character length that the user has entered into the input. This must be input manually as sometimes characters do not count as 1 character. For example an emoji could take up many characters in the input, but should only count as 1 character. If you don't pass <code v-pre>currentLength</code>, the component will use a built-in length calculation.</li>
<li class="d-docsite--list-element"><code v-pre>validate</code>: should be an object with the validation rules to apply to the input. Maximum length validation is supported with the following configuration:</li>
</ul>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token literal-property property">length</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token comment">// describes the maximum length allowed and shown in the label</span></span>
<span class="line">  <span class="token literal-property property">description</span><span class="token operator">:</span> string<span class="token punctuation">,</span>        <span class="token comment">// Required</span></span>
<span class="line">  <span class="token comment">// maximum length allowed to enter</span></span>
<span class="line">  <span class="token literal-property property">max</span><span class="token operator">:</span> number<span class="token punctuation">,</span>                <span class="token comment">// Required</span></span>
<span class="line">  <span class="token comment">// message to show in the warning or error validation message</span></span>
<span class="line">  <span class="token literal-property property">message</span><span class="token operator">:</span> string<span class="token punctuation">,</span>            <span class="token comment">// Required</span></span>
<span class="line">  <span class="token comment">// length from which the validation message will be shown as a warning,</span></span>
<span class="line">  <span class="token comment">// when the maximum length is reached, the validation message will be shown as an error</span></span>
<span class="line">  <span class="token literal-property property">warn</span><span class="token operator">:</span> number<span class="token punctuation">,</span>               <span class="token comment">// Optional</span></span>
<span class="line">  <span class="token comment">// set maxlength attribute, defaults to false</span></span>
<span class="line">  <span class="token literal-property property">limitMaxLength</span><span class="token operator">:</span> boolean<span class="token punctuation">,</span>    <span class="token comment">// Optional</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">If the input is invalid due to the validation, the validation message will be shown even when the input lost focus, otherwise the validation message will be hidden when the user unfocuses the input.</p>
<code-well-header>
  <div class="d-w100p">
    <dt-input
      v-model="inputValue"
      ref="maxLength"
      label="Label"
      placeholder="placeholder"
      :validate="{
        length: {
          description: 'Max 25 characters.',
          message: 'Max 25 characters allowed.',
          max: 25,
          warn: 15,
          limitMaxLength: false,
        }
      }"
    />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.maxLength'
vueCode='
<dt-input
  label="Label"
  placeholder="placeholder"
  :validate="{
    length: {
      description: `Max 25 characters.`,
      message: `Max 25 characters allowed.`,
      max: 25,
      warn: 15,
      limitMaxLength: false,
    }
  }"
/>
'
/>
<h3 id="with-custom-maximum-length-validation-message" tabindex="-1" class="d-docsite--header-3">With Custom Maximum Length Validation Message <a class="header-anchor d-link d-docsite--link d-link" href="#with-custom-maximum-length-validation-message" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w100p">
    <dt-input
      v-model="inputValue"
      label="Label"
      placeholder="placeholder"
      :validate="validate()"
    />
  </div>
</code-well-header>
<code-example-tabs
vueCode='
<dt-input
  label="Label"
  placeholder="placeholder"
  :validate="validate()"
  v-model="inputValue"
/>
'
/>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">const</span> validateData <span class="token operator">=</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token literal-property property">length</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token literal-property property">description</span><span class="token operator">:</span> <span class="token string">'Max 25 characters.'</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token literal-property property">max</span><span class="token operator">:</span> <span class="token number">25</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token literal-property property">warn</span><span class="token operator">:</span> <span class="token number">15</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token literal-property property">limitMaxLength</span><span class="token operator">:</span> <span class="token boolean">false</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token punctuation">}</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">const</span> <span class="token function-variable function">validationMessage</span> <span class="token operator">=</span> <span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token operator">=></span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token keyword">const</span> remainingCharacters <span class="token operator">=</span> validateData<span class="token punctuation">.</span>length<span class="token punctuation">.</span>max <span class="token operator">-</span> currentLength<span class="token punctuation">.</span>value<span class="token punctuation">.</span>length<span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line">  <span class="token keyword">if</span> <span class="token punctuation">(</span>remainingCharacters <span class="token operator">&lt;</span> <span class="token number">0</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token keyword">return</span> <span class="token template-string"><span class="token template-punctuation string">`</span><span class="token interpolation"><span class="token interpolation-punctuation punctuation">${</span>Math<span class="token punctuation">.</span><span class="token function">abs</span><span class="token punctuation">(</span>remainingCharacters<span class="token punctuation">)</span><span class="token interpolation-punctuation punctuation">}</span></span><span class="token string"> characters over limit</span><span class="token template-punctuation string">`</span></span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token punctuation">}</span> <span class="token keyword">else</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token keyword">return</span> <span class="token template-string"><span class="token template-punctuation string">`</span><span class="token interpolation"><span class="token interpolation-punctuation punctuation">${</span>remainingCharacters<span class="token interpolation-punctuation punctuation">}</span></span><span class="token string"> characters left</span><span class="token template-punctuation string">`</span></span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token punctuation">}</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">const</span> <span class="token function-variable function">validate</span> <span class="token operator">=</span> <span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token operator">=></span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token keyword">return</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token literal-property property">length</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">      <span class="token operator">...</span>validateData<span class="token punctuation">.</span>length<span class="token punctuation">,</span></span>
<span class="line">      <span class="token literal-property property">message</span><span class="token operator">:</span> <span class="token function">validationMessage</span><span class="token punctuation">(</span><span class="token punctuation">)</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token punctuation">}</span></span>
<span class="line">  <span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><h3 id="search" tabindex="-1" class="d-docsite--header-3">Search <a class="header-anchor d-link d-docsite--link d-link" href="#search" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>type=&quot;search&quot;</code> with a clear button in the <code v-pre>icon</code> slot. When the input is not empty, the clear button will render and will clear the input field when triggered.</p>
<code-well-header>
  <div class="d-w100p">
    <dt-input
      aria-label="Search items"
      placeholder="Search Items"
      type="search"
      v-model="inputSearchValue"
    >
      <template #leftIcon="{ iconSize }">
        <dt-icon name="search" :size="iconSize" />
      </template>
      <template v-if="inputSearchValue.length !== 0" #rightIcon="{ clear }">
        <dt-button
          kind="muted"
          importance="clear"
          size="xs"
          circle
          aria-label="Clear search"
          @click="clear"
        >
        <template #icon="{ iconSize }">
            <dt-icon name="close" :size="iconSize" />
          </template>
        </dt-button>
      </template>
    </dt-input>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-input__wrapper">
  <span class="base-input__icon--left d-input-icon--left d-input-icon">...</span>
  <input type="search" autocomplete="off" class="base-input__input d-input d-input-icon--left d-input-icon--right" placeholder="Search Items">
  <span class="base-input__icon--right d-input-icon--right d-input-icon undefined" data-qa="dt-input-right-icon-wrapper">
    <button class="base-button__button d-btn d-btn--muted d-btn--xs d-btn--circle d-btn--icon-only" data-qa="dt-button" type="button" aria-label="Clear search">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
    </button>
  </span>
</div>
'
vueCode='
<dt-input
  aria-label="Search items"
  placeholder="Search Items"
  type="search"
  v-model="inputValue"
>
  <template #leftIcon="{ iconSize }">
    <dt-icon name="search" :size="iconSize" />
  </template>
  <template v-if="inputValue.length !== 0" #rightIcon="{ clear }">
    <dt-button
      kind="muted"
      importance="clear"
      size="xs"
      circle
      aria-label="Clear search"
      @click="clear"
    >
      <template #icon="{ iconSize }">
        <dt-icon name="close" :size="iconSize" />
      </template>
    </dt-button>
  </template>
</dt-input>
'
showHtmlWarning />
<h2 id="icon-support" tabindex="-1" class="d-docsite--header-2">Icon Support <a class="header-anchor d-link d-docsite--link d-link" href="#icon-support" aria-hidden="true">#</a></h2>
<code-well-header>
  <div class="d-stack16 d-w100p">
    <dt-input label="Left icon" type="text" placeholder="Placeholder">
      <template #leftIcon="{ iconSize }">
        <dt-icon name="send" :size="iconSize" />
      </template>
    </dt-input>
    <dt-input label="Right icon" type="text" placeholder="Placeholder">
      <template #rightIcon="{ iconSize }">
        <dt-icon name="lock" :size="iconSize" />
      </template>
    </dt-input>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label class="d-label" for="Dialtone--InputExample--IconLeft">Label</label>
  <div class="d-input__wrapper">
    <span class="d-input-icon d-input-icon--left">
      <svg class="d-icon d-icon--size-200">...</svg>
    </span>
    <input class="d-input" id="Dialtone--InputExample--IconLeft" type="text" placeholder="Placeholder" />
  </div>
</div>
<div>
  <label class="d-label" for="Dialtone--InputExample--IconRight">Label</label>
  <div class="d-input__wrapper">
    <input class="d-input" id="Dialtone--InputExample--IconRight" type="text" placeholder="Placeholder" />
    <span class="d-input-icon d-input-icon--right">
      <svg class="d-icon d-icon--size-200">...</svg>
    </span>
  </div>
</div>
'
vueCode='
<dt-input label="Left icon" type="text" placeholder="Placeholder">
  <template #leftIcon="{ iconSize }">
    <dt-icon name="send" :size="iconSize" />
  </template>
</dt-input>
<dt-input label="Right icon" type="text" placeholder="Placeholder">
  <template #rightIcon="{ iconSize }">
    <dt-icon name="lock" :size="iconSize" />
  </template>
</dt-input>
'
showHtmlWarning />
<code-well-header>
  <div class="d-stack16 d-w100p">
    <dt-input label="Left icon" type="textarea" placeholder="Placeholder">
      <template #leftIcon="{ iconSize }">
        <dt-icon name="send" :size="iconSize" />
      </template>
    </dt-input>
    <dt-input label="Right icon" type="textarea" placeholder="Placeholder">
      <template #rightIcon="{ iconSize }">
        <dt-icon name="lock" :size="iconSize" />
      </template>
    </dt-input>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <label class="d-label" for="Dialtone--InputExample--IconLeft">...</label>
  <div class="d-input__wrapper">
    <span class="d-input-icon d-input-icon--left">
      <svg class="d-icon d-icon--size-200">...</svg>
    </span>
    <textarea class="d-textarea" id="Dialtone--InputExample--IconLeft" type="text" placeholder="..."></textarea>
  </div>
</div>
'
vueCode='
<dt-input label="Left icon" type="textarea" placeholder="Placeholder">
  <template #leftIcon="{ iconSize }">
    <dt-icon name="send" :size="iconSize" />
  </template>
</dt-input>
<dt-input label="Right icon" type="textarea" placeholder="Placeholder">
  <template #rightIcon="{ iconSize }">
    <dt-icon name="lock" :size="iconSize" />
  </template>
</dt-input>
'
showHtmlWarning />
<h3 id="icon-sizes" tabindex="-1" class="d-docsite--header-3">Icon Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#icon-sizes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Each Text Input size has a default icon size, keeping it proportional. While rare, customizing the icon size is possible.</p>
<code-well-header>
  <div class="d-stack16 d-w100p">
    <dt-input label="Small input with large icon" type="text" placeholder="Placeholder" size="sm">
      <template #leftIcon>
        <dt-icon name="send" size="400" />
      </template>
    </dt-input>
    <dt-input label="Medium input with extra large icon" type="text" placeholder="Placeholder">
      <template #leftIcon>
        <dt-icon name="send" size="500" />
      </template>
    </dt-input>
    <dt-input label="Extra large input with medium icon" type="text" placeholder="Placeholder" size="xl">
      <template #leftIcon>
        <dt-icon name="send" size="200" />
      </template>
    </dt-input>
    <dt-input label="Large textarea with medium icon" type="textarea" placeholder="Placeholder" icon-size="md" size="lg">
      <template #leftIcon>
        <dt-icon name="send" size="200" />
      </template>
    </dt-input>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div>
  <div>
    <label class="d-label" for="Dialtone--InputExample--IconLeft">Input:sm Icon:lg</label>
  </div>
  <div class="d-input__wrapper">
    <span class="d-input-icon d-input-icon--left">
      <svg class="d-icon d-icon--size-400">...</svg>
    </span>
    <input class="d-input d-input--sm" id="Dialtone--InputExample--IconLeft-sm-lg" type="text" placeholder="Placeholder" />
  </div>
</div>
<div>
  <div>
    <label class="d-label" for="Dialtone--InputExample--IconLeft">Input:md(default), Icon:xl</label>
  </div>
  <div class="d-input__wrapper">
    <span class="d-input-icon d-input-icon--left">
      <svg class="d-icon d-icon--size-500">...</svg>
    </span>
    <input class="d-input" id="Dialtone--InputExample--IconLeft-md-xl" type="text" placeholder="Placeholder" />
  </div>
</div>
<div>
  <div>
    <label class="d-label" for="Dialtone--InputExample--IconLeft">Input:xl Icon:md(default)</label>
  </div>
  <div class="d-input__wrapper">
    <span class="d-input-icon d-input-icon--left">
      <svg class="d-icon d-icon--size-200">...</svg>
    </span>
    <input class="d-input d-input--xl" id="Dialtone--InputExample--IconLeft-xl-md" type="text" placeholder="Placeholder" />
  </div>
</div>
<div>
  <div>
    <label class="d-label" for="Dialtone--InputExample--IconLeft">Textarea:lg Icon:md(default)</label>
  </div>
  <div class="d-input__wrapper">
    <span class="d-input-icon d-input-icon--left">
      <svg class="d-icon d-icon--size-200">...</svg>
    </span>
    <textarea class="d-textarea d-textarea--lg" id="Dialtone--TextareaExample--IconLeft-lg-md" type="text" placeholder="Placeholder"></textarea>
  </div>
</div>
'
vueCode='
<dt-input label="Small input with large icon" type="text" placeholder="Placeholder" size="sm">
  <template #leftIcon>
    <dt-icon name="send" size="400" />
  </template>
</dt-input>
<dt-input label="Medium input with extra large icon" type="text" placeholder="Placeholder">
  <template #leftIcon>
    <dt-icon name="send" size="500" />
  </template>
</dt-input>
<dt-input label="Extra large input with medium icon" type="text" placeholder="Placeholder" size="xl">
  <template #leftIcon>
    <dt-icon name="send" size="200" />
  </template>
</dt-input>
<dt-input label="Large textarea with medium icon" type="textarea" placeholder="Placeholder" size="lg">
  <template #leftIcon>
    <dt-icon name="send" size="200" />
  </template>
</dt-input>
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="input" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="input"></component-class-table><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Make sure the <code v-pre>label</code> <code v-pre>for</code> attribute match the input <code v-pre>id</code>.</li>
<li class="d-docsite--list-element">Avoiding removing <code v-pre>labels</code>. Labelled inputs are user-friendly.</li>
<li class="d-docsite--list-element">Avoid relying on placeholder text as a substitute for a <code v-pre>label</code>.</li>
<li class="d-docsite--list-element">If the input is a required field, use the <code v-pre>aria-required</code> property and use the validation message for input errors.</li>
<li class="d-docsite--list-element">Input with validation errors should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the validation message.</li>
<li class="d-docsite--list-element">Placeholder text should not include critical information. Use description text for any information that helps the user successfully interact with the input.</li>
</ul>
</div></template>


<script setup>
import { ref } from 'vue';

const inputValue = ref('');
const inputSearchValue = ref('Some text');

const messages = {
  warning: { "message": "Warning validation message", "type": "warning" },
  error: { "message": "Error validation message", "type": "error" },
  success: { "message": "Success validation message", "type": "success" },
};

const multipleMessages = [
  { message: 'This is the first message', type: 'error' },
  { message: 'This is the second message', type: 'error' },
  { message: 'This is the third message', type: 'error' },
];

const validateData = {
  length: {
    description: 'Max 25 characters.',
    max: 25,
    warn: 15,
    limitMaxLength: false,
  }
};

const validationMessage = () => {
  const remainingCharacters = validateData.length.max - inputValue.value.length;

  if (remainingCharacters < 0) {
    return `${Math.abs(remainingCharacters)} characters over limit`;
  } else {
    return `${remainingCharacters} characters left`;
  }
};

const validate = () => {
  return {
    length: {
      ...validateData.length,
      message: validationMessage(),
    }
  };
};
</script>